<template>
  <div>
    <div
      v-if="open && isMobile"
      class="sidebar-backdrop"
      @click="$emit('close')"
    ></div>
    <div
      class="chat-sidebar"
      :class="{
        'chat-sidebar--dark': isDarkMode,
        'chat-sidebar--open': open,
        'box-shadow2': !isDarkMode && open
      }"
    >
      <div class="chat-sidebar__actions">
        <button
          type="button"
          class="btn btn-primary chat-sidebar__new-btn"
          @click="newChat"
        >
          <i class="fas fa-plus"></i> New Chat
        </button>
        <button
          type="button"
          class="chat-sidebar__toggle-btn"
          :class="{ 'chat-sidebar__toggle-btn--dark': isDarkMode }"
          aria-label="Close sidebar"
          @click="$emit('toggle')"
        >
          <font-awesome-icon :icon="faChevronLeft" />
        </button>
      </div>

      <div
        class="chat-sidebar__list"
        ref="conversationList"
        @scroll="handleScroll"
      >
        <div
          v-for="conv in conversations"
          :key="conv.id"
          class="chat-sidebar__item"
          :class="{
            'chat-sidebar__item--active': activeConversation && activeConversation.id === conv.id,
            'chat-sidebar__item--dark': isDarkMode
          }"
          @click="selectConversation(conv.id)"
        >
          <span class="chat-sidebar__item-title">{{ formatTitle(conv.title) }}</span>
          <button
            class="chat-sidebar__delete-btn"
            @click.stop="confirmDelete(conv.id)"
            aria-label="Delete conversation"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>

        <div v-if="loading" class="chat-sidebar__loading">
          <div class="spinner spinner-slow"></div>
        </div>

        <div v-if="error && !loading" class="chat-sidebar__error">
          <span>{{ error }}</span>
          <button class="chat-sidebar__retry-btn" @click="retry">Retry</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

export default {
  name: 'ChatSidebar',
  props: {
    open: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isMobile: false,
      faChevronLeft
    };
  },
  computed: {
    ...mapState('chat', ['conversations', 'activeConversation', 'sidebarLoading', 'error', 'pagination']),
    ...mapState('darkMode', ['isDarkMode']),
    loading() {
      return this.sidebarLoading;
    }
  },
  mounted() {
    this.updateMobile();
    window.addEventListener('resize', this.updateMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateMobile);
  },
  methods: {
    updateMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    formatTitle(title) {
      if (!title) return '';
      return title.length > 30 ? title.substring(0, 30) + '…' : title;
    },
    newChat() {
      this.$store.dispatch('chat/clearActiveConversation');
    },
    selectConversation(id) {
      this.$store.dispatch('chat/fetchConversation', id);
      if (this.isMobile) {
        this.$emit('close');
      }
    },
    confirmDelete(id) {
      this.$swal({
        title: 'Delete conversation?',
        text: 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#F28A3E',
        confirmButtonText: 'Delete'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$store.dispatch('chat/deleteConversation', id);
        }
      });
    },
    retry() {
      this.$store.dispatch('chat/fetchConversations', { page: 1 });
    },
    handleScroll() {
      const el = this.$refs.conversationList;
      if (!el) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      if (atBottom && this.pagination.hasMore && !this.loading) {
        this.$store.dispatch('chat/fetchConversations', {
          page: this.pagination.page + 1,
          limit: this.pagination.limit
        });
      }
    }
  }
};
</script>

<style scoped>
.chat-sidebar {
  width: 280px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  flex-shrink: 0;
  overflow: hidden;
  transition: width 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
}

.chat-sidebar:not(.chat-sidebar--open) {
  width: 0;
  border-right-color: transparent;
  pointer-events: none;
}

.chat-sidebar--dark {
  background: #2c3034;
  border-right-color: #555;
}

.chat-sidebar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  flex-shrink: 0;
}

.chat-sidebar__new-btn {
  flex: 1;
  margin: 0;
}

.chat-sidebar__toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f5f6f8;
  color: #555;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.chat-sidebar__toggle-btn:hover {
  background: #eef0f3;
  border-color: #ccc;
  color: #333;
}

.chat-sidebar__toggle-btn--dark {
  background: #3a3f44;
  border-color: #555;
  color: #ccc;
}

.chat-sidebar__toggle-btn--dark:hover {
  background: #464d54;
  border-color: #666;
  color: #fff;
}

.chat-sidebar__list {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px;
}

.chat-sidebar__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  border-left: 3px solid transparent;
  border-radius: 4px;
  margin-bottom: 2px;
  transition: background 0.15s;
}

.chat-sidebar__item:hover {
  background: #f0f0f0;
}

.chat-sidebar__item--dark:hover {
  background: #464d54;
}

.chat-sidebar__item--active {
  border-left-color: #F28A3E;
  font-weight: 600;
}

.chat-sidebar__item-title {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
}

.chat-sidebar__delete-btn {
  display: none;
  background: none;
  border: none;
  color: #c0392b;
  cursor: pointer;
  padding: 2px 6px;
  font-size: 0.85rem;
}

.chat-sidebar__item:hover .chat-sidebar__delete-btn {
  display: inline-flex;
}

.chat-sidebar__loading {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.chat-sidebar__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  color: #c0392b;
  font-size: 0.85rem;
  text-align: center;
}

.chat-sidebar__retry-btn {
  background: #F28A3E;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 14px;
  cursor: pointer;
  font-size: 0.85rem;
}

.chat-sidebar__retry-btn:hover {
  background: #e07a2e;
}

.sidebar-backdrop {
  display: none;
}

@media (max-width: 767px) {
  .chat-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    width: 280px;
    height: 100vh;
    transform: translateX(-100%);
    pointer-events: auto;
  }

  .chat-sidebar:not(.chat-sidebar--open) {
    width: 280px;
    transform: translateX(-100%);
  }

  .chat-sidebar--open {
    transform: translateX(0);
  }

  .sidebar-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1040;
  }
}
</style>
